/**
 * Skill-message chat sessions.
 *
 * A skill's Notify step can send the user a text message with quick replies.
 * The Actions tab lists it; opening it starts a NEW server chat thread that
 * opens with that message as its first assistant turn and the quick replies as
 * chat_intent pills. The user's existing conversation is left untouched and can
 * be returned to. The skill run never waits for the reply — a reply is an
 * ordinary chat turn.
 *
 * The chat model's memory (the LangGraph checkpoint) does not contain the seed
 * message, so every reply sent while the session has no user turn yet carries a
 * hidden context suffix naming the message it answers. The user's bubble shows
 * only what they tapped or typed.
 */

import type { Channel } from '../shared/host';
import type { SkillMessage } from '../notifications/types';

/** What the chat pane needs to show and restore an open skill-message session. */
export interface SkillSessionInfo {
  notificationId: number;
  /** Null while the thread is still being created; only saved once known. */
  threadId: string | null;
  skillName: string;
  title: string;
  body: string;
  receivedAt: string;
  /** The conversation that was open before; null when there was none. */
  previousThreadId: string | null;
}

/** Wire shape of `CreateThreadRequest.seed` (API/models/chat_v2.py). */
export interface ThreadSeed {
  title: string;
  content: string;
  pills: Array<{ label: string; message: string; bubble_text: string }>;
}

const CONTEXT_BODY_MAX_CHARS = 1500;
const SEED_TITLE_MAX_CHARS = 256;

/** Hidden text appended to a reply so the model knows which message it answers. */
export function skillMessageContextSuffix(
  info: Pick<SkillSessionInfo, 'skillName' | 'title' | 'body'>
): string {
  const sender = info.skillName ? `the "${info.skillName}" skill` : 'a skill';
  const body = info.body.slice(0, CONTEXT_BODY_MAX_CHARS);
  return `\n\n[Context: the user is replying to a message sent by ${sender}. Title: ${info.title}. Message: ${body}]`;
}

/** The seed for a message's chat thread: the message, with its quick replies as pills. */
export function buildSkillMessageSeed(message: SkillMessage): ThreadSeed {
  const title = (message.title || message.skillName || 'Message').slice(0, SEED_TITLE_MAX_CHARS);
  const content = message.body ? `**${title}**\n\n${message.body}` : `**${title}**`;
  const suffix = skillMessageContextSuffix({ skillName: message.skillName, title, body: message.body });
  return {
    title,
    content,
    pills: message.quickReplies.map((reply) => ({
      label: reply,
      message: `${reply}${suffix}`,
      bubble_text: reply,
    })),
  };
}

/** "just now", "5 mins ago", "2 hrs ago", "yesterday", "3 days ago". */
export function formatMessageAge(iso: string, now: number = Date.now()): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffMin = Math.floor((now - ts) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return diffMin === 1 ? '1 min ago' : `${diffMin} mins ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return diffHr === 1 ? '1 hr ago' : `${diffHr} hrs ago`;
  const diffDay = Math.floor(diffHr / 24);
  return diffDay === 1 ? 'yesterday' : `${diffDay} days ago`;
}

// ---------------------------------------------------------------------------
// Storage. Both maps are per-viewer conveniences, so every access is guarded:
// storage can be missing or throw (private windows, blocked site data), and the
// feature still works without it — it just cannot reuse a session or show the
// "Previous chat" button after a reload.
// ---------------------------------------------------------------------------

const THREAD_MAP_KEY = 'aiva.skillMessageThreads';
const THREAD_MAP_MAX_ENTRIES = 50;

function readThreadMap(): Array<[number, string]> {
  try {
    const raw = globalThis.localStorage?.getItem(THREAD_MAP_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is [number, string] => Array.isArray(e) && typeof e[0] === 'number' && typeof e[1] === 'string'
    );
  } catch {
    return [];
  }
}

/** The thread a message was opened in before, so re-opening returns to it. */
export function lookupSkillMessageThread(notificationId: number): string | null {
  return readThreadMap().find(([id]) => id === notificationId)?.[1] ?? null;
}

export function rememberSkillMessageThread(notificationId: number, threadId: string): void {
  const entries = readThreadMap().filter(([id]) => id !== notificationId);
  entries.push([notificationId, threadId]);
  try {
    globalThis.localStorage?.setItem(THREAD_MAP_KEY, JSON.stringify(entries.slice(-THREAD_MAP_MAX_ENTRIES)));
  } catch {
    // Best-effort; without it a re-open starts a fresh session.
  }
}

export function forgetSkillMessageThread(notificationId: number): void {
  const entries = readThreadMap().filter(([id]) => id !== notificationId);
  try {
    globalThis.localStorage?.setItem(THREAD_MAP_KEY, JSON.stringify(entries));
  } catch {
    // Best-effort.
  }
}

const sessionKey = (host: Channel | null, contextId: string | null) =>
  `aiva.skillSession.${host ?? 'none'}.${contextId ?? ''}`;

function isSessionInfo(value: unknown): value is SkillSessionInfo {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.notificationId === 'number' && typeof v.threadId === 'string' && typeof v.skillName === 'string'
  );
}

export function loadSkillSession(host: Channel | null, contextId: string | null): SkillSessionInfo | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(sessionKey(host, contextId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isSessionInfo(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveSkillSession(
  host: Channel | null,
  contextId: string | null,
  info: SkillSessionInfo | null
): void {
  try {
    const store = globalThis.sessionStorage;
    if (!store) return;
    if (info) store.setItem(sessionKey(host, contextId), JSON.stringify(info));
    else store.removeItem(sessionKey(host, contextId));
  } catch {
    // Best-effort; the session still works for this pane's lifetime.
  }
}
